"use client";

import Avatar from "@/components/Avatar/Avatar";
import { useAidaStore } from "@/components/providers/stores/AidaStoreProvider";
import { COOKIE_NAMES } from "@/constants/cookies";
import useSession from "@/hooks/useSession";
import { cn } from "@/lib/utils";
import trackPxEvent from "@/utils/mixpanel";
import { retry } from "@/utils/retry";
import { Icon } from "@iconify/react";
import * as Sentry from "@sentry/nextjs";
import { getCookie } from "cookies-next";
import { Drawer } from "flowbite-react";
import { useCallback, useMemo, useState } from "react";

// This pattern looks for messages that start with a number, dot, and space, followed by bold text
// Visually, this creates numbered list items with bold headings
// Example: "1. **Heading**"
const NUMBERED_BOLD_PATTERN = /^\d+\.\s+\*\*.*?\*\*$/;

// This pattern splits the text at the markdown bold markers
// Visually, this creates bold text
// Example: "**Bold Text**"
const MARKDOWN_BOLD_DELIMITER = /\*\*/;

// Separate message component to prevent unnecessary re-renders
const Message = ({
  content,
  role,
  isLoading = false,
  id,
  onFeedback
}: {
  content: string;
  role: "user" | "assistant";
  isLoading?: boolean;
  id?: string;
  onFeedback?: (feedback: "positive" | "negative") => void;
}) => {
  const [lastFeedback, setLastFeedback] = useState<"positive" | "negative" | null>(null);
  const formatMessage = useCallback((content: string) => {
    const parts = content.split(/(\d+\.\s+\*\*.*?\*\*)/g);
    return parts.map((part, index) => {
      if (part.match(NUMBERED_BOLD_PATTERN)) {
        const [number, ...rest] = part.split(MARKDOWN_BOLD_DELIMITER);
        const boldText = rest[0];
        const description = rest[1]?.trim();
        return <div key={index} className="mb-4">
            <div className="font-semibold text-primary-900">
              {number}
              {boldText}
            </div>
            {description && <div className="mt-1 text-neutral-700">{description}</div>}
          </div>;
      }
      return <div key={index}>{part}</div>;
    });
  }, []);
  const formattedContent = useMemo(() => role === "assistant" ? formatMessage(content) : content, [content, role, formatMessage]);
  const handleFeedbackClick = useCallback((feedback: "positive" | "negative") => {
    setLastFeedback(feedback);
    onFeedback?.(feedback);
  }, [onFeedback]);
  return <div className={cn("flex items-start gap-3 mb-6 w-full animate-fade-in-slide", {
    "justify-end items-end": role === "user",
    "justify-start items-start": role === "assistant"
  })} data-sentry-component="Message" data-sentry-source-file="AidaWindow.tsx">
      {role === "assistant" && <div className="bg-linear-to-r from-blue-500 to-purple-600 rounded-full p-1.5">
          <Icon icon="heroicons-solid:sparkles" className="h-5 w-5 text-white" />
        </div>}
      <div className={cn("rounded-2xl p-3 text-paragraph-small relative", {
      "bg-primary-500 text-dark-800 max-w-[80%] rounded-br-none": role === "user",
      "bg-neutral-100 text-neutral-900 max-w-[80%] rounded-tl-none": role === "assistant"
    })}>
        {formattedContent}
        {isLoading ? <Icon icon="svg-spinners:3-dots-fade" className="w-10 h-6 text-primary-300" /> : role === "assistant" && !isLoading && <div className="flex items-center gap-1 mt-2 justify-end">
              <button className={cn("p-1.5 hover:bg-neutral-200 rounded-full transition-colors", lastFeedback === "positive" && "text-primary-500")} onClick={() => handleFeedbackClick("positive")}>
                <Icon icon={lastFeedback === "positive" ? "bi:hand-thumbs-up-fill" : "bi:hand-thumbs-up"} className="h-4 w-4" />
              </button>
              <div className="h-4 w-px bg-neutral-300" />
              <button className={cn("p-1.5 hover:bg-neutral-200 rounded-full transition-colors", lastFeedback === "negative" && "text-primary-500")} onClick={() => handleFeedbackClick("negative")}>
                <Icon icon={lastFeedback === "negative" ? "bi:hand-thumbs-down-fill" : "bi:hand-thumbs-down"} className="h-4 w-4" />
              </button>
            </div>}
      </div>
      {role === "user" && <Avatar menu={false} />}
    </div>;
};
export const AidaWindow = () => {
  // Separate store selectors to prevent unnecessary re-renders
  const isOpen = useAidaStore(state => state.isOpen);
  const toggleChat = useAidaStore(state => state.toggleChat);
  const messages = useAidaStore(state => state.messages);
  const isLoading = useAidaStore(state => state.isLoading);
  const addMessage = useAidaStore(state => state.addMessage);
  const updateMessage = useAidaStore(state => state.updateMessage);
  const setLoading = useAidaStore(state => state.setLoading);
  const session = useSession();
  const [inputValue, setInputValue] = useState("");
  const [textareaHeight, setTextareaHeight] = useState("auto");
  const handleSubmit = useCallback(async (e: React.FormEvent) => {
    e.preventDefault();
    if (!inputValue.trim() || isLoading) return;
    const userMessage = inputValue.trim();
    setInputValue("");
    addMessage({
      content: userMessage,
      role: "user"
    });
    setLoading(true);
    try {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 30000); // 30 second timeout

      const response = await retry(async () => {
        const res = await fetch("/api/ai-chat-messages", {
          method: "POST",
          headers: {
            "Content-Type": "application/json"
          },
          body: JSON.stringify({
            message: userMessage
          }),
          signal: controller.signal
        });
        if (!res.ok) {
          throw new Error(`HTTP error! status: ${res.status}`);
        }
        return res;
      }, {
        maxAttempts: 3,
        initialDelay: 1000,
        maxDelay: 5000,
        backoffFactor: 2,
        shouldRetry: error => {
          // Only retry if the error is a 400 or 500 error
          return error.message.includes("400") || error.message.includes("500");
        }
      });
      clearTimeout(timeoutId);
      const reader = response.body?.getReader();
      if (!reader) throw new Error("No reader available");
      let accumulatedContent = "";
      let assistantMessageId: string | null = null;
      while (true) {
        const {
          done,
          value
        } = await reader.read();
        const chunk = new TextDecoder().decode(value);
        accumulatedContent += chunk;
        if (!assistantMessageId) {
          assistantMessageId = Date.now().toString();
          addMessage({
            content: accumulatedContent,
            role: "assistant",
            id: assistantMessageId,
            timestamp: Date.now()
          });
        } else {
          updateMessage(assistantMessageId, accumulatedContent);
        }
        if (done) break;
      }

      // Wait for the cookie to be set
      await new Promise(resolve => setTimeout(resolve, 100));

      // Get the updated threadID after the cookie is set
      const updatedThreadID = getCookie(COOKIE_NAMES.AI_CHATBOT_ASSISTANT_THREAD_ID, {
        path: "/"
      });

      // Track message sent and reply received with the updated threadID
      trackPxEvent({
        event: "ASSISTANT_SENT_MESSAGE",
        email: session?.email ?? "",
        currentUrl: window.location.href,
        messageContent: userMessage,
        threadID: updatedThreadID as string,
        messageId: assistantMessageId ?? "",
        messageReceived: accumulatedContent
      });
      trackPxEvent({
        event: "ASSISTANT_RECEIVED_REPLY",
        email: session?.email ?? "",
        currentUrl: window.location.href,
        replyContent: accumulatedContent,
        threadID: updatedThreadID as string,
        messageId: assistantMessageId ?? "",
        messageSent: userMessage
      });

      // Track when Aida can't find information in the materials
      if (accumulatedContent.includes("not able to find that information")) {
        trackPxEvent({
          event: "AI_CHAT_NO_INFORMATION_FOUND",
          messageId: assistantMessageId ?? "",
          messageSent: userMessage,
          messageReceived: accumulatedContent
        });
      }
    } catch (error) {
      console.error("Error:", error);
      Sentry.captureException(error);
      addMessage({
        content: "Sorry, I encountered an error. Please try again.",
        role: "assistant"
      });
    } finally {
      setLoading(false);
    }
  }, [inputValue, isLoading, addMessage, updateMessage, setLoading, session]);
  const handleTextareaChange = useCallback((e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setInputValue(e.target.value);

    // Reset height to auto to get the correct scrollHeight
    e.target.style.height = "auto";

    // Calculate the new height
    const newHeight = Math.min(e.target.scrollHeight, 200); // 200px is our max-h-[200px]

    // Set the new height
    e.target.style.height = `${newHeight}px`;

    // If content exceeds max height, show scrollbar
    if (e.target.scrollHeight > 200) {
      e.target.style.overflowY = "auto";
    } else {
      e.target.style.overflowY = "hidden";
    }
  }, []);
  const handleKeyDown = useCallback((e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSubmit(e as unknown as React.FormEvent);
    }
  }, [handleSubmit]);
  const handleFeedback = useCallback(({
    feedback,
    messageId,
    messageSent,
    messageReceived
  }: {
    feedback: "positive" | "negative";
    messageId: string;
    messageSent: string;
    messageReceived: string;
  }) => {
    trackPxEvent({
      event: "AI_CHAT_FEEDBACK",
      feedback,
      messageId,
      messageSent,
      messageReceived
    });
  }, []);
  const memoizedMessages = useMemo(() => {
    return <>
        {messages.map((message, index) => <Message key={message.id} content={message.content} role={message.role} isLoading={isLoading && index === messages.length - 1 && message.role === "assistant"} id={message.id} onFeedback={feedback => handleFeedback({
        feedback,
        messageId: message.id ?? "",
        messageSent: messages[index - 1]?.content ?? "",
        messageReceived: message.content
      })} />)}
        {isLoading && messages[messages.length - 1]?.role === "user" && <Message content="" role="assistant" isLoading={true} />}
      </>;
  }, [messages, isLoading, handleFeedback]);
  return <Drawer open={isOpen} onClose={() => {
    toggleChat();
    trackPxEvent({
      event: "ASSISTANT_CLOSED",
      email: session?.email ?? "",
      currentUrl: window.location.href
    });
  }} position="right" className="w-[500px] grid grid-cols-1 p-0" style={{
    gridTemplateRows: "max-content 1fr"
  }} data-sentry-element="Drawer" data-sentry-component="AidaWindow" data-sentry-source-file="AidaWindow.tsx">
      <header className="flex flex-row items-center gap-3 border-b p-6 border-neutral-100">
        <div className="bg-linear-to-r from-blue-500 to-purple-600 rounded-full p-2 cursor-pointer">
          <Icon icon="heroicons-solid:sparkles" className="h-6 w-6 text-white" data-sentry-element="Icon" data-sentry-source-file="AidaWindow.tsx" />
        </div>
        <div className="flex flex-col">
          <div className="flex items-center gap-2">
            <span className="text-paragraph-large text-neutral-900 font-semibold">
              Aida
            </span>
            <Icon icon="fa:circle" className="h-2 w-2 text-success-500" data-sentry-element="Icon" data-sentry-source-file="AidaWindow.tsx" />
          </div>
          <span className="text-sm text-neutral-500">
            Your AI assistant is here to help
          </span>
        </div>

        <Icon icon="bi:x" width={24} height={24} className="text-neutral-500 ml-auto cursor-pointer hover:text-neutral-700 transition-colors" onClick={() => {
        trackPxEvent({
          event: "ASSISTANT_CLOSED",
          email: session?.email ?? "",
          currentUrl: window.location.href
        });
        toggleChat();
      }} data-sentry-element="Icon" data-sentry-source-file="AidaWindow.tsx" />
      </header>

      <div className="flex-1 overflow-y-auto p-6">{memoizedMessages}</div>

      <form onSubmit={handleSubmit} className="border-t p-6">
        <div className="flex space-x-2">
          <textarea value={inputValue} onChange={handleTextareaChange} onKeyDown={handleKeyDown} placeholder="Type your message..." className="flex-1 rounded-lg border border-neutral-300 px-4 py-2 focus:border-neutral-800 focus:outline-hidden resize-none min-h-lg max-h-[200px]" disabled={isLoading} rows={1} style={{
          height: textareaHeight
        }} />
          <button type="submit" disabled={isLoading || !inputValue.trim()} className={cn("rounded-lg bg-neutral-800 px-4 py-2 text-white h-lg self-end", "hover:bg-neutral-700 focus:outline-hidden focus:ring-2 focus:ring-neutral-800 focus:ring-offset-2", "disabled:bg-neutral-300 disabled:cursor-not-allowed")}>
            Send
          </button>
        </div>
      </form>
    </Drawer>;
};